/* @img-migrated */
import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Heart, MessageSquare, Lock, ArrowRight, Loader2, AlertCircle, ShieldCheck, Sparkles } from 'lucide-react';
import { LeadershipBadge } from '../components/LeadershipBadge';
import { PostImageCarousel } from '../components/PostImageCarousel';
import { SharePostMenu } from '../components/community/SharePostMenu';
import { Share2 } from 'lucide-react';

import Img from '../components/Img';
import { resolveImg } from '../lib/images';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const formatTime = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  } catch { return iso; }
};

const Avatar = ({ user, size = 'w-12 h-12' }) => {
  if (user?.picture) return <Img variant="avatar" src={resolveImg(user.picture)} alt="" className={`${size} rounded-full object-cover ring-2 ring-white shadow-sm`} />;
  return (
    <div className={`${size} rounded-full bg-gradient-to-br from-[#002249] to-[#0a3570] flex items-center justify-center text-white font-bold text-lg shadow-sm`}>
      {user?.name?.charAt(0).toUpperCase() || '?'}
    </div>
  );
};

const PublicPostPage = () => {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/community/post/${id}/public`);
        setPost(res.data.post);
      } catch (err) {
        setError(err?.response?.status === 404 ? 'not_found' : 'error');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 flex items-center justify-center">
        <Loader2 className="w-7 h-7 text-[#002249] animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 flex items-center justify-center px-4" data-testid="public-post-error">
        <div className="max-w-md w-full bg-white rounded-2xl border border-slate-200 p-8 text-center shadow-[0_10px_40px_-12px_rgba(0,34,73,0.12)]">
          <div className="w-14 h-14 rounded-2xl bg-rose-50 text-rose-500 mx-auto flex items-center justify-center mb-4">
            <AlertCircle className="w-6 h-6" />
          </div>
          <h1 className="text-[18px] font-bold text-[#002249] mb-1.5">
            {error === 'not_found' ? 'This post is no longer available' : 'Could not load post'}
          </h1>
          <p className="text-[13.5px] text-slate-500 leading-relaxed">
            {error === 'not_found'
              ? 'The post may have been removed by its author or by an administrator.'
              : 'Please check your connection and try again.'}
          </p>
          <div className="mt-6 flex flex-col gap-2">
            <Link to="/" className="h-10 inline-flex items-center justify-center rounded-lg bg-[#002249] hover:bg-[#001530] text-white text-[13px] font-semibold transition-colors">
              Back to DCCF
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const author = post?.author || {};
  const company = post?.company || null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100" data-testid="public-post-page">
      {/* Top bar */}
      <header className="sticky top-0 z-20 bg-white/85 backdrop-blur-sm border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="public-post-logo">
            <span className="text-[13px] font-bold tracking-tight text-[#002249]">DCCF NETWORK</span>
            <span className="hidden sm:inline text-[11px] text-slate-400">· Dominican Chamber of Commerce of Florida</span>
          </Link>
          <Link to="/member-login" className="text-[12.5px] font-semibold text-[#002249] hover:underline" data-testid="public-post-signin-top">
            Sign in
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Post Card */}
        <motion.article
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl border border-slate-200 shadow-[0_4px_24px_-8px_rgba(0,34,73,0.08)] overflow-hidden"
          data-testid="public-post-card"
        >
          {/* Header */}
          <div className="flex items-start justify-between px-5 sm:px-6 pt-5 pb-3">
            <div className="flex items-start gap-3 min-w-0">
              <Avatar user={author} />
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-1.5">
                  <p className="text-[15px] font-bold text-[#050505] leading-tight">{author.name || 'Member'}</p>
                  {author.leadership_level && (
                    <LeadershipBadge leadershipLevel={author.leadership_level} leadershipTitle={author.leadership_title} />
                  )}
                  {author.is_founding_member && !author.leadership_level && (
                    <span className="text-[9px] font-bold bg-amber-100 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded-full">Founder</span>
                  )}
                </div>
                <p className="text-[12.5px] text-slate-500 mt-0.5">
                  {company?.name && <span>{company.name} · </span>}
                  {formatTime(post.created_at)}
                </p>
              </div>
            </div>
            {/* Share */}
            <div className="relative">
              <button
                onClick={() => setShareOpen(!shareOpen)}
                className="h-9 w-9 rounded-full hover:bg-slate-100 text-slate-500 hover:text-[#002249] flex items-center justify-center transition-colors"
                data-testid="public-post-share"
                aria-label="Share"
              >
                <Share2 className="w-[18px] h-[18px]" />
              </button>
              {shareOpen && (
                <SharePostMenu postId={post.id} postText={post.content} onClose={() => setShareOpen(false)} />
              )}
            </div>
          </div>

          {/* Content */}
          {post.content && (
            <div className="px-5 sm:px-6 pb-3">
              <p className="text-[15.5px] sm:text-[16px] text-[#050505] leading-relaxed whitespace-pre-wrap">{post.content}</p>
            </div>
          )}

          {/* Images */}
          {post.images?.length > 0 && (
            <PostImageCarousel images={post.images} maxHeight="max-h-[640px]" dataTestId="public-post-carousel" />
          )}

          {/* Engagement summary (read-only) */}
          <div className="px-5 sm:px-6 py-3 flex items-center gap-5 border-t border-slate-100 text-[13px] text-slate-500">
            <span className="flex items-center gap-1.5">
              <Heart className="w-4 h-4" /> {post.likes_count || 0}
            </span>
            <span className="flex items-center gap-1.5">
              <MessageSquare className="w-4 h-4" /> {post.comments_count || 0}
            </span>
          </div>
        </motion.article>

        {/* CTA Block */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mt-6 bg-gradient-to-br from-[#002249] to-[#001530] rounded-2xl p-6 sm:p-8 text-white shadow-[0_10px_30px_-12px_rgba(0,34,73,0.4)]"
          data-testid="public-post-cta"
        >
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
              <Lock className="w-5 h-5 text-amber-300" />
            </div>
            <div>
              <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-amber-300">Members-only interaction</p>
              <h2 className="text-[18px] sm:text-[20px] font-bold tracking-tight mt-1">Join the DCCF Network to interact and connect.</h2>
              <p className="text-[13.5px] text-white/75 mt-2 leading-relaxed">
                Sign in or apply for membership to like, comment, message members, and join exclusive events.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-5 text-[12.5px] text-white/80">
            <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-emerald-300" /> Verified members only</div>
            <div className="flex items-center gap-2"><MessageSquare className="w-4 h-4 text-sky-300" /> Direct messaging</div>
            <div className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-amber-300" /> Curated events & deals</div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2.5">
            <Link
              to="/member-login"
              className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-lg bg-white text-[#002249] hover:bg-white/95 text-[13.5px] font-bold transition-all hover:scale-[1.01]"
              data-testid="public-post-login-cta"
            >
              Login
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/membership"
              className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-lg bg-[#E63946] hover:bg-[#c02a35] text-white text-[13.5px] font-bold transition-all hover:scale-[1.01]"
              data-testid="public-post-apply-cta"
            >
              Apply for Membership
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </motion.div>

        {/* Footer */}
        <p className="text-[11.5px] text-slate-400 text-center mt-8">
          © Dominican Chamber of Commerce of Florida · <Link to="/" className="hover:underline">dominicanchamberfl.org</Link>
        </p>
      </main>
    </div>
  );
};

export default PublicPostPage;
